
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import { Getter, namespace } from 'vuex-class';
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import vSelect from "vue-select";
import { parseTaskText, truncateString } from '@/filters';
import { ActionCategoriesExpandedState, ActionPlan, ActionPlanTask, DecoratedActionCategories, ScriptCategoryLink, TaskCategory, TaskType } from '@/store/tasks/types';
import { orderBy } from 'lodash';
import TaskTypeIds from '@/types/sv-data/enums/TaskTypeIds';
import { BModal, BvModalEvent } from 'bootstrap-vue';
import ConfirmationModal from '@/components/mobile-raise-templates/ConfirmationModal.vue';
import { NumberToNumberMap, NumberToStringMap } from '@/store/types';

const Tasks = namespace("tasks");

@Component({
    components: {
        "vue-select": vSelect,
        "vue-perfect-scrollbar": VuePerfectScrollbar,
        "b-modal": BModal,
        "confirmation-modal": ConfirmationModal
    },
    filters: {
        parseTaskText,
        truncateString
    }
})
export default class ActionPlansModal extends Vue {

    @Prop({ type: Boolean, default: false})
    private showModal: boolean;

    @Getter getFeature: (featureList: string[]) => boolean;

    @Tasks.Action fetchTaskTypes: () => Promise<void>;
    @Tasks.Action fetchActionPlans: () => Promise<void>;
    @Tasks.Action fetchTaskCategories: () => Promise<void>;
    @Tasks.Action fetchActionLibrary: () => Promise<void>;
    @Tasks.Action getScriptCategoryLinks: () => Promise<void>;

    @Tasks.Getter("getTaskLibrary") taskLibrary: ActionPlanTask[];
    @Tasks.Getter("getActionPlans") actionPlans: ActionPlan[];
    @Tasks.Getter("getTaskCategories") taskCategories: TaskCategory[];
    @Tasks.Getter("getTaskTypes") taskTypes: TaskType[];
    @Tasks.Getter("getScriptCategoryLinks") scriptCategoryLinks: ScriptCategoryLink[];

    private selectedActionPlan: ActionPlan | null = null;
    private actionsInActionPlansByCategory: NumberToNumberMap = {};
    private actionCategoriesExpandedState: ActionCategoriesExpandedState = {};
    private taskTypeIds = TaskTypeIds;
    private isModalShown: boolean = false;

    private async mounted(): Promise<void> {
        await this.fetchTaskCategories();
        await this.fetchActionPlans();
        await this.fetchActionLibrary();
        await this.fetchTaskTypes();
        this.setDefaultSelectedActionPlan();
        this.initActionsInActionPlansByCategoryPlaceholder();
        this.initActionCategoriesExpandedState();

        if (this.isReorderCategoriesEnabled) {
            await this.getScriptCategoryLinks();
        }
    }

    private get isReorderCategoriesEnabled(): boolean {
        return this.getFeature(["Actions", "ReorderCategories"]);
    }

    private get taskTypesByTitle(): NumberToStringMap {
        let taskTypes: NumberToStringMap = {};
        this.taskTypes.forEach(type => {
            taskTypes[type.taskTypeID] = type.title;
        });
        return taskTypes;
    }
    
    private get decoratedActionCategoriesInCurrentScript(): DecoratedActionCategories {
        let relevantCategoryIDs = [];
        if (this.selectedActionPlan.scriptID) {
            relevantCategoryIDs = this.taskLibrary
                .filter(t => this.selectedActionPlan.scriptID in t.scripts)
                .map(t => t.taskCategoryID)
                .filter((value, index, self) => self.indexOf(value) === index);
        }

        const decoratedActionCategories: DecoratedActionCategories = {};
        this.taskCategories.forEach(cat => {
            if (relevantCategoryIDs.includes(cat.taskCategoryID)) {
                decoratedActionCategories[cat.taskCategoryID] = {
                    title: cat.title,
                    totalNumActions: this.taskLibrary.filter(t => t.taskCategoryID === cat.taskCategoryID).length,
                    numActionsAddedToScript: this.taskLibrary.filter(t => {
                        return (
                            t.taskCategoryID === cat.taskCategoryID &&
                            this.selectedActionPlan.scriptID in t.scripts
                        );
                    }).length
                };
            }
        });

        return decoratedActionCategories;
    }

    @Watch("showModal")
    private onShowModalChanged(): void {
        this.isModalShown = this.showModal;
    }

    @Watch("actionsPlans")
    @Watch("taskLibrary")
    private initActionsInActionPlansByCategoryPlaceholder(): void {
        this.actionsInActionPlansByCategory = {};
        // 1st level nesting by action plan ID
        this.actionPlans.forEach(p => {
            // 2nd level nesting by action category id
            this.taskLibrary
                .filter(task => p.scriptID in task.scripts)
                .map(task => task.taskCategoryID)
                .filter((value, index, self) => self.indexOf(value) === index)
                .forEach(categoryID => {

                    if (!this.actionsInActionPlansByCategory[p.scriptID]) {
                        this.actionsInActionPlansByCategory[p.scriptID] = {};
                    }
                    this.actionsInActionPlansByCategory[p.scriptID][categoryID] = this.taskLibrary.filter(
                        task => p.scriptID in task.scripts && task.taskCategoryID === categoryID
                    );
                });
        });
    }

    @Watch("actionsInActionPlansByCategory")
    private initActionCategoriesExpandedState(): void {
        this.actionPlans.forEach(actionPlan => {
            if (!this.actionCategoriesExpandedState[actionPlan.scriptID]) {
                Vue.set(this.actionCategoriesExpandedState, actionPlan.scriptID, {});
            }

            if (this.actionsInActionPlansByCategory[actionPlan.scriptID]) {
                Object.entries(this.actionsInActionPlansByCategory[actionPlan.scriptID]).forEach(
                    ([categoryID, _]) => {
                        Vue.set(this.actionCategoriesExpandedState[actionPlan.scriptID], categoryID, true);
                    }
                );
            }
        });
    }

    private actionPlanCategories(scriptId: number): ScriptCategoryLink[] {
        const actionPlanTasksInScript = this.actionsInActionPlansByCategory[scriptId];
        const unorderedActionPlanTasks = orderBy(actionPlanTasksInScript, category => category.taskCategoryID, ["asc"]);
        const unorderedCategories: ActionPlanTask[] = [].concat.apply([], unorderedActionPlanTasks);

        let orderedCategories: ScriptCategoryLink[] = [];

        if (!this.isReorderCategoriesEnabled) {
            let unorderedCategoryLinks: ScriptCategoryLink[] = [];

            unorderedCategories.forEach((actionPlanTask: ActionPlanTask) => {
                if (!unorderedCategoryLinks.some(x => x.taskCategoryId === actionPlanTask.taskCategoryID)) {
                    let unorderedCategoryToAdd: ScriptCategoryLink = {
                        scriptId: scriptId,
                        taskCategoryId: actionPlanTask.taskCategoryID,
                        order: null
                    };

                    unorderedCategoryLinks.push(unorderedCategoryToAdd)
                }
            });
            return unorderedCategoryLinks;
        } else {
            unorderedCategories.forEach((actionPlanTask: ActionPlanTask) => {
                // Default ScriptCategoryLinkOrder to use if the ScriptCategoryLink cannot be obtained
                const scriptCategoryLinkOrderDefault = 100;

                let scriptCategoryLink = this.getScriptCategoryLinkById(scriptId, actionPlanTask.taskCategoryID);
                let scriptCategoryLinkOrder: number = scriptCategoryLinkOrderDefault;

                if (scriptCategoryLink != null) {
                    scriptCategoryLinkOrder = scriptCategoryLink.order
                }

                actionPlanTask.scriptCategoryLink = scriptCategoryLink;

                if (!orderedCategories.some(x => x.taskCategoryId === actionPlanTask.taskCategoryID)) {
                    let orderedCategoryToAdd: ScriptCategoryLink = {
                        scriptId: scriptId,
                        taskCategoryId: actionPlanTask.taskCategoryID,
                        order: scriptCategoryLinkOrder
                    };

                    orderedCategories.push(orderedCategoryToAdd)
                }
            });
        }

        return orderBy(orderedCategories, category => category.order, ["asc"]);
    }

    private getScriptCategoryLinkById(scriptId: number, taskCategoryId: number): ScriptCategoryLink {
        const scriptCategoryLinks: ScriptCategoryLink[] = this.scriptCategoryLinks;
        let scriptCategoryLink = null;

        if (scriptCategoryLinks) {
            scriptCategoryLink = scriptCategoryLinks.find(x => x.scriptId === scriptId && x.taskCategoryId === taskCategoryId);
        }

        return scriptCategoryLink;
    }

    private setDefaultSelectedActionPlan(): void {
        if (this.actionPlans.length > 0) {
            this.selectedActionPlan = this.actionPlans[0];
        }
    }

    private collapseCategory(actionCategoryID: number): void {
        Vue.set(this.actionCategoriesExpandedState[this.selectedActionPlan.scriptID], actionCategoryID, false);
    }

    private expandCategory(actionCategoryID: number): void {
        Vue.set(this.actionCategoriesExpandedState[this.selectedActionPlan.scriptID], actionCategoryID, true);
    }

    private orderedActionPlanByCategory(scriptId: number, categoryId: number): ActionPlanTask[] {
        const unorderedActions = this.actionsInActionPlansByCategory[scriptId][categoryId] as ActionPlanTask[];
        return orderBy(unorderedActions, action => action.scripts[scriptId].order, ["asc"]);
    }
    
    @Emit("actionPlanSelected")
    private actionPlanSelected(): ActionPlan {
        const selectedActionPlan = {...this.selectedActionPlan};
        this.setDefaultSelectedActionPlan();
        this.isModalShown = false;
        return selectedActionPlan;
    }

    @Emit("closeModal")
    private closeModal(): void {
        this.setDefaultSelectedActionPlan();
        return;
    }

    // Ok and Cancel handled by closeModal
    // but headerclose (x button top right) will need to be triggered.
    private hideModal(bvEvent: BvModalEvent): void {
        const headerCloseTrigger = "headerclose";
        if (bvEvent.trigger === headerCloseTrigger) {
            this.setDefaultSelectedActionPlan();
            this.$emit("closeModal");
        }            
    }
}

